'use client';

/**
 * VisualComponentTemplate - The Universal Container for All Visual Components
 * 
 * This template provides:
 * - SEO-optimized structured data
 * - Accessibility features
 * - Export capabilities (PNG, SVG, embed code)
 * - Social sharing
 * - Responsive design
 * - Professional styling
 * 
 * Every visual component should be wrapped in this template for consistency
 */

import React, { useRef, useState, useId, useEffect, useMemo } from 'react';
import { MoreVertical, Download, Share2, Maximize, Code, FileImage, FileCode, X } from '@/lib/icons';
import { downloadAsPNG, copyToClipboard } from '../utils/export';
import { generateStructuredData } from '../utils/seo';
import { BRAND } from '../constants/brand';
import { DIMENSIONS } from '../constants/dimensions';
import { DynamicDimensions } from './DynamicDimensions';
import type { CalloutConfig as DynamicCalloutConfig } from './DynamicDimensions';
import { ResponsiveCanvas } from './ResponsiveCanvas';
import type { SmartLayoutConfig } from './SmartLayout.types';
import { SmartLayoutEngine } from './SmartLayoutEngine';
import { SmartRenderer } from './SmartRenderer';
import { DownloadProgress } from '../components/DownloadProgress';

export interface SEOConfig {
  title: string;
  description: string;
  keywords?: string[];
  author?: string;
  datePublished?: string;
  dateModified?: string;
}

export interface CalloutConfig {
  title?: string;
  content?: string;
  emphasis?: string;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  titleColor?: string;
}

export interface FooterConfig {
  showCopyright?: boolean;
}

export interface HeaderConfig {
  title?: string;
  subtitle?: string;
  badge?: string;
}

export interface VisualComponentTemplateProps {
  // Identity
  name: string;
  id?: string;
  className?: string;
  
  // Dimensions - NOW DYNAMIC
  dimensions?: { width: number; height: number };
  size?: 'inline' | 'full' | 'social';
  preset?: 'social-twitter' | 'social-facebook' | 'social-linkedin' | 'social-instagram-square' | 
           'content-inline' | 'content-full' | 'content-sidebar' | 
           'presentation-16:9' | 'presentation-4:3' | 'default';
  autoHeight?: boolean; // Allow height to grow with content
  contentHeight?: number; // Specify content area height for auto-calculation
  
  // Content
  children: React.ReactNode;
  header?: HeaderConfig;
  callout?: CalloutConfig; // Single callout (legacy support)
  callouts?: DynamicCalloutConfig[]; // Multiple callouts (new)
  footer?: FooterConfig;
  
  // Responsive behavior
  enableResponsiveScaling?: boolean;
  scalingMode?: 'proportional' | 'responsive' | 'fluid';
  
  // Features
  actions?: ('download-png' | 'share' | 'expand' | 'embed')[];
  showBorder?: boolean;
  
  // SEO/Export
  seo: SEOConfig;
  exportFilename?: string;
  
  // Behavior
  onShare?: () => void;
  onDownload?: (format: 'png') => void;
  onExpand?: () => void;
  analytics?: boolean;
  
  // Smart Layout (OPTIONAL - defaults to disabled for backwards compatibility)
  smartLayout?: SmartLayoutConfig;
}

export function VisualComponentTemplate({
  name,
  id,
  className = '',
  dimensions,
  size = 'inline',
  preset,
  autoHeight = false,
  contentHeight = 350,
  children,
  header,
  callout,
  callouts,
  footer = { showCopyright: true },
  enableResponsiveScaling = true,
  scalingMode = 'responsive',
  actions = ['download-png', 'expand'], // Removed 'share' until PNG auto-generation is built
  showBorder = true,
  seo,
  exportFilename,
  onShare,
  onDownload,
  onExpand,
  analytics = true,
  smartLayout
}: VisualComponentTemplateProps) {
  const generatedId = useId();
  const componentId = id || `visual-${name}-${generatedId}`;
  const svgRef = useRef<SVGSVGElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadMessage, setDownloadMessage] = useState('');
  const [isDownloading, setIsDownloading] = useState(false);
  
  // Smooth modal close handler
  const handleCloseModal = () => {
    setIsAnimating(false);
    // Delay hiding modal to allow exit animation
    setTimeout(() => {
      setIsExpanded(false);
    }, 200); // Match animation duration
  };
  
  // ESC key handler for fullscreen modal
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isExpanded) {
        handleCloseModal();
      }
    };
    
    if (isExpanded) {
      document.addEventListener('keydown', handleEscape);
    }
    
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isExpanded]);

  // Animation timing effect
  useEffect(() => {
    if (isExpanded) {
      // Small delay to trigger enter animation after DOM update
      setTimeout(() => setIsAnimating(true), 10);
    } else {
      // Reset animation state when modal closes
      setIsAnimating(false);
    }
  }, [isExpanded]);
  
  // Merge single callout with callouts array for backward compatibility
  const allCallouts = callouts || (callout ? [callout] : []);
  
  // Calculate dimensions using DynamicDimensions FIRST
  let width: number;
  let height: number;
  let positions: any;
  
  // ALWAYS use DynamicDimensions for bulletproof positioning
  const config = {
    preset: preset || 'default',
    customWidth: dimensions?.width || 700, // FORCE 700px width if not specified
    customHeight: autoHeight ? undefined : dimensions?.height, // Only use custom height if NOT auto
    header: header,
    content: {
      type: 'custom',
      height: contentHeight
    },
    callouts: allCallouts,
    footer: footer
  };
  
  const calculated = DynamicDimensions.getDimensions(config);
  width = calculated.width;
  height = calculated.height;
  positions = DynamicDimensions.getElementPositions(config);
  
  // Calculate responsive fullscreen size with useMemo
  const fullscreenSize = useMemo(() => {
    const aspectRatio = height / width;
    
    if (typeof window === 'undefined') {
      return { width: 700, height: 700 * aspectRatio };
    }
    
    if (window.innerWidth >= 1280) {
      // Desktop: 1200px max width
      return { width: 1200, height: 1200 * aspectRatio };
    } else if (window.innerWidth >= 768) {
      // Tablet: 900px max width  
      return { width: 900, height: 900 * aspectRatio };
    } else {
      // Mobile: 95% of viewport width
      const modalWidth = window.innerWidth * 0.95;
      return { width: modalWidth, height: modalWidth * aspectRatio };
    }
  }, [width, height, isExpanded]); // Recalculate when expanded or dimensions change
  
  // Validate layout
  const validation = DynamicDimensions.validate(config);
  if (!validation.valid && process.env.NODE_ENV === 'development') {
    console.warn('Layout validation warnings:', validation.errors);
  }
  
  // SMART LAYOUT INTEGRATION - Phase 3 (after dimensions are calculated)
  let smartRenderPlan: any = null;
  let useSmartRendering = false;
  
  // Only use smart layout if explicitly enabled AND configured properly
  if (smartLayout?.enabled && SmartLayoutEngine.isReady(smartLayout)) {
    try {
      // Auto-detect elements if none provided
      let elementsToAnalyze = smartLayout.elements;
      if (!elementsToAnalyze || elementsToAnalyze.length === 0) {
        elementsToAnalyze = SmartLayoutEngine.autoDetectElements(
          children,
          { width, height },
          {
            scanDepth: 3,
            minTextLength: 3,
            confidenceThreshold: 0.6
          }
        );
      }
      
      // Generate smart render plan
      if (elementsToAnalyze.length > 0) {
        smartRenderPlan = SmartLayoutEngine.generateRenderPlan(
          elementsToAnalyze,
          { width, height },
          smartLayout,
          1 // Global scale - could be made configurable
        );
        useSmartRendering = true;
        
        // Debug logging in development
        if (smartLayout.debug && process.env.NODE_ENV === 'development') {
          console.log('🧠 Smart Layout Plan Generated:', smartRenderPlan);
        }
      }
    } catch (error) {
      // Smart layout failed - fall back to existing system
      console.warn('Smart layout failed, falling back to legacy system:', error);
      useSmartRendering = false;
      smartRenderPlan = null;
    }
  }
  
  // Dynamic year for copyright
  const currentYear = new Date().getFullYear();
  
  // Generate structured data for SEO
  const structuredData = generateStructuredData({
    type: 'ImageObject',
    name: seo.title,
    description: seo.description,
    keywords: seo.keywords,
    author: seo.author || 'LeadPops',
    datePublished: seo.datePublished,
    dateModified: seo.dateModified || new Date().toISOString(),
    contentUrl: `https://leadpops.com/visuals/${name}`,
    encodingFormat: 'image/svg+xml',
    width: width.toString(),
    height: height.toString()
  });
  
  const handleDownloadPNG = async () => {
    if (svgRef.current && !isDownloading) {
      setIsDownloading(true);
      setDownloadProgress(0);
      setDownloadMessage('Preparing download...');
      
      try {
        await downloadAsPNG(
          svgRef.current, 
          exportFilename || name,
          (progress, message) => {
            setDownloadProgress(progress);
            setDownloadMessage(message);
          }
        );
        
        onDownload?.('png');
        
        if (analytics) {
          // Track download event
          if (typeof window !== 'undefined' && 'gtag' in window) {
            (window as any).gtag('event', 'download_visual', {
              visual_name: name,
              format: 'png'
            });
          }
        }
        
        // Wait a bit before hiding the progress
        setTimeout(() => {
          setIsDownloading(false);
          setDownloadProgress(0);
        }, 400);
      } catch (error) {
        console.error('Download failed:', error);
        setIsDownloading(false);
        setDownloadProgress(0);
      }
    }
  };
  
  
  const handleShare = () => {
    if (onShare) {
      onShare();
    } else {
      // Default share behavior
      const shareUrl = `${window.location.origin}/visuals/${name}`;
      const shareText = `Check out this ${seo.title} from LeadPops`;
      
      if (navigator.share) {
        navigator.share({
          title: seo.title,
          text: shareText,
          url: shareUrl
        });
      } else {
        // Fallback to Twitter
        window.open(
          `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
          '_blank'
        );
      }
    }
    
    if (analytics) {
      if (typeof window !== 'undefined' && 'gtag' in window) {
        (window as any).gtag('event', 'share_visual', { visual_name: name });
      }
    }
  };
  
  const handleExpand = () => {
    setIsExpanded(true);
    // isAnimating will be set to true in useEffect after DOM update
    onExpand?.();
    
    if (analytics) {
      if (typeof window !== 'undefined' && 'gtag' in window) {
        (window as any).gtag('event', 'expand_visual', { visual_name: name });
      }
    }
  };
  
  const handleCopyEmbed = () => {
    const embedCode = `<iframe src="https://leadpops.com/embed/${name}" width="${width}" height="${height}" frameborder="0" title="${seo.title}"></iframe>`;
    copyToClipboard(embedCode);
    
    if (analytics) {
      if (typeof window !== 'undefined' && 'gtag' in window) {
        (window as any).gtag('event', 'copy_embed', { visual_name: name });
      }
    }
  };
  
  return (
    <>
      {/* Download Progress Overlay */}
      <DownloadProgress 
        isVisible={isDownloading} 
        progress={downloadProgress} 
        message={downloadMessage} 
      />
      
      {/* Structured Data for SEO */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      
      <div className="visual-component-container mx-auto relative" style={{ width: width, maxWidth: '100%' }}>
        <svg
          ref={svgRef}
          id={componentId}
          viewBox={`0 0 ${width} ${height}`}
          width={width}
          height={height}
          style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
          role="img"
          aria-labelledby={`${componentId}-title ${componentId}-desc`}
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <style>
              {`
                text {
                  font-family: var(--font-text), -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                }
              `}
            </style>
          </defs>
          
          {/* SEO Metadata */}
          <title id={`${componentId}-title`}>{seo.title}</title>
          <desc id={`${componentId}-desc`}>{seo.description}</desc>
          
          {/* Hidden text for crawlers - CRITICAL FOR SEO */}
          <text className="sr-only" x="-9999" y="-9999">
            {seo.title}. {seo.description}
            {seo.keywords?.join(', ')}
            Created by LeadPops - Mortgage Lead Generation Platform.
          </text>
          
          {/* Container Border */}
          {showBorder && (
            <rect
              x="2"
              y="2"
              width={width - 4}
              height={height - 4}
              fill="white"
              stroke={BRAND.colors.border}
              strokeWidth="2"
              rx="8"
            />
          )}
          
          {/* Optional Header */}
          {header && (
            <g className="visual-header">
              <rect
                x="2"
                y="2"
                width={width - 4}
                height="50"
                fill={BRAND.colors.backgroundAlt}
                rx="8"
                ry="8"
              />
              <text
                x={width / 2}
                y="20"
                textAnchor="middle"
                fontSize="16"
                fontWeight="700"
                fill={BRAND.colors.text.primary}
              >
                {header.title}
              </text>
              {header.subtitle && (
                <text
                  x={width / 2}
                  y="38"
                  textAnchor="middle"
                  fontSize="12"
                  fontWeight="400"
                  fill={BRAND.colors.text.secondary}
                >
                  {header.subtitle}
                </text>
              )}
            </g>
          )}
          
          {/* THE CANVAS - Visual Content Goes Here */}
          <g className="visual-canvas">
            {enableResponsiveScaling ? (
              <ResponsiveCanvas 
                width={width} 
                height={height}
                scalingMode={scalingMode}
              >
                {useSmartRendering && smartRenderPlan ? (
                  <SmartRenderer 
                    renderPlan={smartRenderPlan}
                    containerDimensions={{ width, height }}
                    className="smart-layout"
                  />
                ) : (
                  children
                )}
              </ResponsiveCanvas>
            ) : (
              useSmartRendering && smartRenderPlan ? (
                <SmartRenderer 
                  renderPlan={smartRenderPlan}
                  containerDimensions={{ width, height }}
                  className="smart-layout"
                />
              ) : (
                children
              )
            )}
          </g>
          
          {/* Callout Boxes - NOW SUPPORTS MULTIPLE */}
          {allCallouts.length > 0 && positions.callouts && (
            <g className="visual-callouts">
              {allCallouts.map((calloutItem, index) => {
                const pos = positions.callouts[index];
                if (!pos) return null;
                
                // Calculate text positions within this callout - VISUALLY CENTERED
                const lineHeight = 14.7; // Match the line height from DynamicDimensions
                const totalTextHeight = lineHeight * 3 + 2 * 2; // 3 lines + 2 gaps
                const mathematicalPadding = (pos.height - totalTextHeight) / 2;
                // Add extra padding for visual centering (SVG text baseline adjustment)
                const visualAdjustment = 15; // Extra pixels for better visual appearance
                const verticalPadding = mathematicalPadding + visualAdjustment;
                let currentTextY = pos.y + verticalPadding; // Start with visual centering
                
                // Text positioning calculated
                
                const textY = {
                  title: currentTextY,
                  content: currentTextY + (calloutItem.title ? lineHeight + 2 : 0), // Only add if title exists
                  emphasis: currentTextY + (calloutItem.title ? lineHeight + 2 : 0) + (calloutItem.content ? lineHeight + 2 : 0)
                };
                
                return (
                  <g key={index} className="visual-callout">
                    <rect
                      x={pos.x}
                      y={pos.y}
                      width={pos.width}
                      height={pos.height}
                      fill={calloutItem.backgroundColor || 'rgba(16, 185, 129, 0.1)'}
                      stroke={calloutItem.borderColor || BRAND.colors.success}
                      strokeWidth="2"
                      rx="8"
                    />
                    
                    {calloutItem.title && (
                      <text
                        key={`title-${index}-${textY.title}`}
                        x={width / 2}
                        y={textY.title}
                        textAnchor="middle"
                        fontSize="13"
                        fontWeight="700"
                        fill={calloutItem.titleColor || BRAND.colors.text.secondary}
                      >
                        {calloutItem.title}
                      </text>
                    )}
                    
                    {calloutItem.content && (
                      <text
                        key={`content-${index}-${textY.content}`}
                        x={width / 2}
                        y={textY.content}
                        textAnchor="middle"
                        fontSize="11"
                        fontWeight="500"
                        fill={BRAND.colors.text.secondary}
                      >
                        {calloutItem.content}
                      </text>
                    )}
                    
                    {calloutItem.emphasis && (
                      <text
                        key={`emphasis-${index}-${textY.emphasis}`}
                        x={width / 2}
                        y={textY.emphasis}
                        textAnchor="middle"
                        fontSize="12"
                        fontWeight="700"
                        fill={calloutItem.textColor || BRAND.colors.success}
                      >
                        {calloutItem.emphasis}
                      </text>
                    )}
                  </g>
                );
              })}
            </g>
          )}

          {/* Footer Strip - RESPONSIVE based on width */}
          {footer?.showCopyright && (
            <g className="visual-footer">
              {/* Footer background strip - USES DYNAMIC POSITION */}
              <rect
                x="3"
                y={positions.footer.y}
                width={width - 6}
                height="27"
                fill={BRAND.colors.backgroundMuted}
                rx="5"
                ry="5"
              />
              
              {/* RESPONSIVE FOOTER CONTENT */}
              {width >= 600 ? (
                // FULL FOOTER (600px+)
                <>
                  <text
                    x="20"
                    y={positions.footer.copyrightY}
                    fontSize="10"
                    fontWeight="400"
                    fill={BRAND.colors.text.muted}
                  >
                    © {currentYear} LeadPops by rebel iQ • Mortgage Lead Generation Platform
                  </text>
                  
                  <text
                    x={width - 45}
                    y={positions.footer.copyrightY}
                    textAnchor="end"
                    fontSize="11"
                    fontWeight="500"
                    fill={BRAND.colors.primary}
                  >
                    LeadPops.com
                  </text>
                  
                  <image
                    x={positions.footer.faviconX}
                    y={positions.footer.faviconY - 3}
                    width="16"
                    height="16"
                    xlinkHref="/favicons/favicon-32x32.webp"
                    preserveAspectRatio="xMidYMid meet"
                  />
                </>
              ) : width >= 350 ? (
                // COMPACT FOOTER (350-599px) - FIXED TO SHOW ALL CONTENT
                <>
                  <text
                    x="10"
                    y={positions.footer.copyrightY}
                    fontSize="8"
                    fontWeight="400"
                    fill={BRAND.colors.text.muted}
                  >
                    © {currentYear} LeadPops by rebel iQ
                  </text>
                  
                  <text
                    x={width - 35}
                    y={positions.footer.copyrightY}
                    textAnchor="end"
                    fontSize="9"
                    fontWeight="500"
                    fill={BRAND.colors.primary}
                  >
                    LeadPops.com
                  </text>
                  
                  <image
                    x={width - 30}
                    y={positions.footer.faviconY - 3}
                    width="12"
                    height="12"
                    xlinkHref="/favicons/favicon-32x32.webp"
                    preserveAspectRatio="xMidYMid meet"
                  />
                </>
              ) : (
                // MINI FOOTER (under 350px)
                <text
                  x={width / 2}
                  y={positions.footer.copyrightY}
                  textAnchor="middle"
                  fontSize="8"
                  fontWeight="400"
                  fill={BRAND.colors.text.muted}
                >
                  © {currentYear} LeadPops by rebel iQ
                </text>
              )}
            </g>
          )}
        </svg>
        
        {/* Actions Menu */}
        {actions.length > 0 && (
          <div 
            className="absolute top-2 right-2"
            onMouseLeave={() => setMenuOpen(false)}
          >
            <button
              onClick={() => setMenuOpen(!menuOpen)}
              onMouseEnter={() => setMenuOpen(true)}
              className="p-2 rounded-lg bg-white/90 backdrop-blur-sm shadow-md hover:shadow-lg transition-all"
              aria-label="Visual actions menu"
            >
              <MoreVertical className="w-4 h-4 text-primary" />
            </button>
            
            {/* Smooth animated dropdown menu */}
            <div className={`absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 transition-all duration-200 origin-top-right ${
              menuOpen 
                ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto' 
                : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
            }`}>
              {actions.includes('download-png') && (
                <button
                  onClick={handleDownloadPNG}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
                >
                  <FileImage className="w-4 h-4" />
                  Download PNG
                </button>
              )}
              {actions.includes('share') && (
                <button
                  onClick={handleShare}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
                >
                  <Share2 className="w-4 h-4" />
                  Share
                </button>
              )}
              {actions.includes('expand') && (
                <button
                  onClick={handleExpand}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
                >
                  <Maximize className="w-4 h-4" />
                  View Fullscreen
                </button>
              )}
              {actions.includes('embed') && (
                <button
                  onClick={handleCopyEmbed}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-2 text-sm transition-colors"
                >
                  <Code className="w-4 h-4" />
                  Copy Embed Code
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Responsive Fullscreen Modal */}
        {isExpanded && (
          <div
            className={`fixed bg-black/80 z-50 flex items-center justify-center p-4 md:p-8 transition-opacity duration-200 ${
              isAnimating ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '100vw',
              height: '100vh'
            }}
            onClick={(e) => {
              if (e.target === e.currentTarget) handleCloseModal();
            }}
          >
            <div 
              className={`relative transition-all duration-200 ${
                isAnimating ? 'scale-100 opacity-100' : 'scale-95 opacity-0'
              }`}
              style={{ width: fullscreenSize.width, height: fullscreenSize.height }}
            >
            {/* Close button */}
            <button
              onClick={handleCloseModal}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 flex items-center gap-2 transition-colors duration-200"
            >
              <span className="text-sm">Close</span>
              <X className="w-8 h-8" />
            </button>
            
            {/* Pure component - no background, no padding */}
            <svg
              viewBox={`0 0 ${width} ${height}`}
              width={fullscreenSize.width}
              height={fullscreenSize.height}
              style={{ display: 'block', maxWidth: '100%', height: 'auto' }}
              xmlns="http://www.w3.org/2000/svg"
              className="transition-all duration-200 drop-shadow-2xl"
            >
                  <defs>
                    <style>
                      {`
                        text {
                          font-family: var(--font-text), -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                        }
                      `}
                    </style>
                  </defs>
                  
                  {/* Container Border */}
                  {showBorder && (
                    <rect
                      x="2"
                      y="2"
                      width={width - 4}
                      height={height - 4}
                      fill="white"
                      stroke={BRAND.colors.border}
                      strokeWidth="2"
                      rx="8"
                    />
                  )}
                  
                  {/* THE CANVAS - Visual Content */}
                  <g className="visual-canvas">
                    {enableResponsiveScaling ? (
                      <ResponsiveCanvas 
                        width={width} 
                        height={height}
                        scalingMode={scalingMode}
                      >
                        {useSmartRendering && smartRenderPlan ? (
                          <SmartRenderer 
                            renderPlan={smartRenderPlan}
                            containerDimensions={{ width, height }}
                            className="smart-layout"
                          />
                        ) : (
                          children
                        )}
                      </ResponsiveCanvas>
                    ) : (
                      useSmartRendering && smartRenderPlan ? (
                        <SmartRenderer 
                          renderPlan={smartRenderPlan}
                          containerDimensions={{ width, height }}
                          className="smart-layout"
                        />
                      ) : (
                        children
                      )
                    )}
                  </g>
                  
                  {/* Callout Boxes - DYNAMIC in lightbox too */}
                  {allCallouts.length > 0 && positions.callouts && (
                    <g className="visual-callouts">
                      {allCallouts.map((calloutItem, index) => {
                        const pos = positions.callouts[index];
                        if (!pos) return null;
                        
                        // Calculate text positions - VISUALLY CENTERED (matching main render)
                        const lineHeight = 14.7;
                        const totalTextHeight = lineHeight * 3 + 2 * 2; // 3 lines + 2 gaps
                        const mathematicalPadding = (pos.height - totalTextHeight) / 2;
                        // Add extra padding for visual centering (SVG text baseline adjustment)
                        const visualAdjustment = 15; // Extra pixels for better visual appearance
                        const verticalPadding = mathematicalPadding + visualAdjustment;
                        let currentTextY = pos.y + verticalPadding;
                        
                        // DEBUG: Log the actual values being used (LIGHTBOX)
                        console.log('TEXT POSITIONING DEBUG (LIGHTBOX):', {
                          posY: pos.y,
                          posHeight: pos.height,
                          totalTextHeight,
                          verticalPadding,
                          currentTextY
                        });
                        
                        const textY = {
                          title: currentTextY,
                          content: currentTextY + (calloutItem.title ? lineHeight + 2 : 0),
                          emphasis: currentTextY + (calloutItem.title ? lineHeight + 2 : 0) + (calloutItem.content ? lineHeight + 2 : 0)
                        };
                        
                        return (
                          <g key={index} className="visual-callout">
                            <rect
                              x={pos.x}
                              y={pos.y}
                              width={pos.width}
                              height={pos.height}
                              fill={calloutItem.backgroundColor || 'rgba(16, 185, 129, 0.1)'}
                              stroke={calloutItem.borderColor || BRAND.colors.success}
                              strokeWidth="2"
                              rx="8"
                            />
                            
                            {calloutItem.title && (
                              <text
                                key={`title-${index}-${textY.title}`}
                                x={width / 2}
                                y={textY.title}
                                textAnchor="middle"
                                fontSize="13"
                                fontWeight="700"
                                fill={calloutItem.titleColor || BRAND.colors.text.secondary}
                              >
                                {calloutItem.title}
                              </text>
                            )}
                            
                            {calloutItem.content && (
                              <text
                                key={`content-${index}-${textY.content}`}
                                x={width / 2}
                                y={textY.content}
                                textAnchor="middle"
                                fontSize="11"
                                fontWeight="500"
                                fill={BRAND.colors.text.secondary}
                              >
                                {calloutItem.content}
                              </text>
                            )}
                            
                            {calloutItem.emphasis && (
                              <text
                                key={`emphasis-${index}-${textY.emphasis}`}
                                x={width / 2}
                                y={textY.emphasis}
                                textAnchor="middle"
                                fontSize="12"
                                fontWeight="700"
                                fill={calloutItem.textColor || BRAND.colors.success}
                              >
                                {calloutItem.emphasis}
                              </text>
                            )}
                          </g>
                        );
                      })}
                    </g>
                  )}
                  
                  {/* Footer Strip - RESPONSIVE in lightbox too */}
                  {footer?.showCopyright && (
                    <g className="visual-footer">
                      <rect
                        x="3"
                        y={positions.footer.y}
                        width={width - 6}
                        height="27"
                        fill={BRAND.colors.backgroundMuted}
                        rx="5"
                        ry="5"
                      />
                      
                      {/* RESPONSIVE FOOTER CONTENT */}
                      {width >= 600 ? (
                        // FULL FOOTER
                        <>
                          <text
                            x="20"
                            y={height - 12}
                            fontSize="10"
                            fontWeight="400"
                            fill={BRAND.colors.text.muted}
                          >
                            © {currentYear} LeadPops by rebel iQ • Mortgage Lead Generation Platform
                          </text>
                          
                          <text
                            x={width - 45}
                            y={height - 12}
                            textAnchor="end"
                            fontSize="11"
                            fontWeight="500"
                            fill={BRAND.colors.primary}
                          >
                            LeadPops.com
                          </text>
                          
                          <image
                            x={width - 40}
                            y={height - 25}
                            width="16"
                            height="16"
                            xlinkHref="/favicons/favicon-32x32.webp"
                            preserveAspectRatio="xMidYMid meet"
                          />
                        </>
                      ) : width >= 400 ? (
                        // COMPACT FOOTER
                        <>
                          <text
                            x="15"
                            y={height - 12}
                            fontSize="9"
                            fontWeight="400"
                            fill={BRAND.colors.text.muted}
                          >
                            © {currentYear} LeadPops by rebel iQ
                          </text>
                          
                          <text
                            x={width - 15}
                            y={height - 12}
                            textAnchor="end"
                            fontSize="10"
                            fontWeight="500"
                            fill={BRAND.colors.primary}
                          >
                            LeadPops.com
                          </text>
                        </>
                      ) : (
                        // MINI FOOTER
                        <text
                          x={width / 2}
                          y={height - 12}
                          textAnchor="middle"
                          fontSize="9"
                          fontWeight="400"
                          fill={BRAND.colors.text.muted}
                        >
                          © {currentYear} LeadPops by rebel iQ.com
                        </text>
                      )}
                    </g>
                  )}
            </svg>
            </div>
          </div>
        )}
      </div>
    </>
  );
}